import { ApolloError } from "@apollo/client";
import { Action } from "../../types/reducer-action";
import { parseCommonError } from "../../utils/commons";

export interface DataEntryState<T, S> {
  dataSet?: T[];
  selectedDataId?: number;
  dataDetails?: Readonly<S>;
  form: S;
  errors: Record<string, string>;
  subdomains: Record<string, DataEntryState<unknown, unknown>>;
}

/**
 * Common event names for data entry tasks.
 * For cross-domain reducer, add a domain prefix.
 * eg. addresses/setEntryList, contacts/setCurrentEntry
 */
export enum DataEntryEvent {
  SetStateValues = '/setStateValue',
  NewDataSet = '/newDataSet',
  UpdateData = '/updateData',
  SelectData = '/selectData',
  SetDataDetails = '/setDataDetails',
  NewForm = '/newForm',
  UpdateForm = '/updateForm',
  ResetForm = '/resetForm',
  AddErrors = '/addErrors'
}

export const dataEntryReducer = <T, S>(state: DataEntryState<T, S>, action: Action, idField = 'id'): DataEntryState<T, S> => {
  const separatorPos = action.type.indexOf('/');

  if (separatorPos > 0) {
    const domain = action.type.substring(0, separatorPos);
    const event = action.type.substring(separatorPos);

    return {
      ...state,
      subdomains: {
        ...state.subdomains,
        [domain]: dataEntryReducer(state.subdomains[domain], {
          ...action,
          type: event
        } as Action)
      }
    }
  }

  switch (action.type) {
    case DataEntryEvent.SetStateValues:
      return {
        ...state,
        ...action.payload
      };

    case DataEntryEvent.NewDataSet:
      return {
        ...state,
        dataSet: action.payload,
        selectedDataId: undefined,
        dataDetails: undefined,
        form: {} as S,
        errors: {},
        subdomains: {}
      };

    case DataEntryEvent.UpdateData: {
      const newEntry = action.payload;
      if (!newEntry) return state;

      const dataSet = state.dataSet as Record<string, any>[];

      const newList = dataSet?.map(item => item[idField] === newEntry[idField] ? { ...item, ...newEntry } : item) ?? [];
      if (!newList.some(item => item[idField] === newEntry[idField])) {
        newList.push(newEntry);
      }

      return {
        ...state,
        dataSet: newList
      };
    }

    case DataEntryEvent.SelectData:
      return action.payload === state?.selectedDataId ? { ...state } : {
        ...state,
        selectedDataId: action.payload,
        dataDetails: undefined,
        form: {} as S,
        errors: {},
        subdomains: {}
      };

    case DataEntryEvent.SetDataDetails: {
      return {
        ...state,
        dataDetails: action.payload,
        form: action.payload,
        errors: {}
      };
    }

    case DataEntryEvent.NewForm:
      return {
        ...state,
        selectedDataId: undefined,
        dataDetails: undefined,
        form: action.payload ?? {} as S,
        errors: {},
        subdomains: {}
      };

    case DataEntryEvent.ResetForm:
      return {
        ...state,
        form: { ...state.dataDetails } as S,
        errors: {},
        subdomains: {}
      };

    case DataEntryEvent.UpdateForm: {
      const form = { ...state.form } as Record<string, any>;
      const payload = action.payload;
      const errors = state.errors;

      let diff = false;
      for (let key in payload) {
        if (payload[key] !== form[key]) {
          diff = true;
          delete errors[key];
        }
      }

      if (!diff) return state;

      return {
        ...state,
        form: {
          ...form,
          ...payload
        },
        errors: errors
      };
    }

    case DataEntryEvent.AddErrors: {
      return {
        ...state,
        errors: {
          ...state.errors,
          ...action.payload
        }
      };
    }

    default: {
      throw new Error('Unknown action type: ' + action.type);
    }
  }
};

export class FormUpdateAction extends Action {
  constructor(event: React.ChangeEvent<HTMLInputElement>, domain = '') {
    const { name, value, type } = event.target;

    super(domain + DataEntryEvent.UpdateForm,
      { [name]: (type === 'checkbox' ? event.target.checked : value) }
    );
  }
};

export class FormErrorAction extends Action {
  constructor(event: React.InvalidEvent<HTMLInputElement>, domain = '') {
    const msg = parseCommonError(event);

    super(domain + DataEntryEvent.AddErrors,
      { [event.target.name]: msg }
    );
  }
};

export class ApolloErrorAction extends Action {
  constructor(error: ApolloError, domain = '') {
    const field = error.graphQLErrors[0].extensions['field'] as string;
    if (!field) {
      console.error(error);
    }

    super(domain + DataEntryEvent.AddErrors,
      field ? { [field]: error.message } : undefined
    );
  }
};
